import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../context/AdminAuthContext";
import AdminLibraryPanel from "../components/AdminLibraryPanel";
import AdminMusicPanel from "../components/AdminMusicPanel";
import AdminAnalyticsPanel from "../components/AdminAnalyticsPanel";

const TABS = [
  { key: "all", label: "All comments" },
  { key: "flagged", label: "Flagged queue" },
  { key: "library", label: "Library" },
  { key: "music", label: "Music" },
  { key: "analytics", label: "Analytics" },
];

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Admin() {
  const { admin, logout, adminAxios } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Redirect to login when not authenticated.
  useEffect(() => {
    if (admin === false) navigate("/admin/login", { replace: true });
  }, [admin, navigate]);

  const load = useCallback(async () => {
    if (!adminAxios) return;
    if (tab === "library" || tab === "analytics" || tab === "music") {
      // These tabs manage their own fetching.
      setLoading(false);
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const path = tab === "flagged" ? "/admin/comments/flagged" : "/admin/comments?limit=500";
      const res = await adminAxios.get(path);
      setRows(res.data?.comments || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not load comments.");
    } finally {
      setLoading(false);
    }
  }, [adminAxios, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (id, kind) => {
    if (!adminAxios) return;
    setBusyId(id);
    setError("");
    setInfo("");
    try {
      if (kind === "hide") {
        await adminAxios.post(`/admin/comments/${id}/hide`);
        setInfo("Hidden.");
      } else if (kind === "unhide") {
        await adminAxios.post(`/admin/comments/${id}/unhide`);
        setInfo("Restored.");
      } else if (kind === "delete") {
        if (!window.confirm("Delete this comment permanently? Replies will also be removed.")) {
          setBusyId("");
          return;
        }
        await adminAxios.delete(`/admin/comments/${id}`);
        setInfo("Deleted.");
      }
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Action failed.");
    } finally {
      setBusyId("");
    }
  };

  if (admin === null) {
    return (
      <main className="relative min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="rm-cm-bg" aria-hidden="true" />
        <div className="relative z-10 text-[#F0E6D2]/70 font-body">Verifying admin session…</div>
      </main>
    );
  }
  if (admin === false) return null;

  return (
    <main className="relative min-h-screen" data-testid="admin-page">
      <div className="rm-cm-bg" aria-hidden="true" />

      <header className="relative z-10 px-6 sm:px-12 py-8 flex items-center justify-between">
        <Link
          to="/"
          className="font-cinzel text-xs sm:text-sm tracking-[0.28em] uppercase text-[#FFE7A0]/85 hover:text-[#FFE7A0]"
          data-testid="admin-home-link"
        >
          ← Matrix
        </Link>
        <div className="flex items-center gap-5">
          <span
            className="font-cinzel text-[10px] sm:text-xs tracking-[0.28em] uppercase text-[#F0E6D2]/65"
            data-testid="admin-email-badge"
          >
            {admin.email}
          </span>
          <button
            type="button"
            className="rm-cm-link"
            onClick={async () => {
              setError("");
              setInfo("");
              try {
                const res = await adminAxios.get("/admin/app-download-method.md", {
                  responseType: "blob",
                });
                const cd = res.headers["content-disposition"] || "";
                const m = cd.match(/filename="([^"]+)"/);
                const filename = m ? m[1] : "app-download-method.md";
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                setInfo(`Downloaded ${filename}.`);
              } catch (err) {
                const detail = err?.response?.data?.detail;
                setError(typeof detail === "string" ? detail : "Download failed.");
              }
            }}
            data-testid="admin-download-app-method-btn"
            title="Download the markdown explainer that describes how the Restoration Matrix is distributed as a PWA."
          >
            App download method
          </button>
          <button
            type="button"
            className="rm-cm-link"
            onClick={async () => {
              // One-click Matrix Logic Snapshot download. The endpoint
              // is admin-only and returns a multi-hundred-KB markdown
              // payload as an attachment; pasting the URL into a
              // browser tab returns 401 because the bearer token is
              // not carried, so we use the already-authenticated
              // `adminAxios` and trigger the file download by hand
              // from the response Blob — single click, no token
              // copy-paste.
              setError("");
              setInfo("");
              try {
                const res = await adminAxios.get("/admin/matrix-logic.md", {
                  responseType: "blob",
                });
                // Derive filename from the Content-Disposition header
                // the server sets (matrix-logic-YYYYMMDD-HHMMSS.md).
                // Fall back to a generic name if the header is absent.
                const cd = res.headers["content-disposition"] || "";
                const m = cd.match(/filename="([^"]+)"/);
                const filename = m ? m[1] : "matrix-logic.md";
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                setInfo(`Downloaded ${filename}.`);
              } catch (err) {
                const detail = err?.response?.data?.detail;
                setError(typeof detail === "string" ? detail : "Snapshot download failed.");
              }
            }}
            data-testid="admin-download-matrix-logic-btn"
            title="Download a fresh markdown snapshot of every active rule, brain, and classifier dictionary."
          >
            Logic snapshot
          </button>
          <button
            type="button"
            className="rm-cm-link"
            onClick={() => {
              logout();
              navigate("/admin/login", { replace: true });
            }}
            data-testid="admin-logout-btn"
          >
            Sign out
          </button>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 pb-24">
        <div className="text-center mb-10">
          <div
            className="font-cinzel text-[10px] sm:text-xs tracking-[0.32em] uppercase text-[#FFE7A0]/80 mb-5"
            data-testid="admin-overline"
          >
            Community moderation
          </div>
          <h1
            className="font-serif-display italic text-4xl sm:text-5xl text-[#FFE7A0]"
            data-testid="admin-title"
          >
            Tend the room.
          </h1>
        </div>

        <nav className="rm-admin-tabs" role="tablist" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={tab === t.key}
              className={`rm-admin-tab${tab === t.key ? " rm-admin-tab-active" : ""}`}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {error && (
          <div className="rm-cm-alert rm-cm-alert-error mt-6" data-testid="admin-error">
            {error}
          </div>
        )}
        {info && (
          <div className="rm-cm-alert rm-cm-alert-info mt-6" data-testid="admin-info">
            {info}
          </div>
        )}

        {tab === "library" ? (
          <div className="mt-8">
            <AdminLibraryPanel adminAxios={adminAxios} />
          </div>
        ) : tab === "music" ? (
          <div className="mt-8">
            <AdminMusicPanel adminAxios={adminAxios} />
          </div>
        ) : tab === "analytics" ? (
          <div className="mt-8">
            <AdminAnalyticsPanel adminAxios={adminAxios} />
          </div>
        ) : (
          <div className="mt-8" data-testid="admin-list">
          {loading && (
            <div className="rm-cm-empty" data-testid="admin-loading-row">
              Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="rm-cm-empty" data-testid="admin-empty">
              {tab === "flagged" ? "No flagged comments." : "No comments yet."}
            </div>
          )}
          {!loading && rows.length > 0 && (
            <ul className="rm-admin-rows">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className={`rm-admin-row${r.hidden ? " rm-admin-row-hidden" : ""}`}
                  data-testid={`admin-row-${r.id}`}
                >
                  <div className="rm-admin-row-meta">
                    <span className="rm-cm-name">{r.display_name}</span>
                    <span className="rm-cm-time">{formatTime(r.created_at)}</span>
                    <span
                      className={`rm-admin-badge${r.parent_id ? " rm-admin-badge-reply" : ""}`}
                      data-testid="admin-row-kind"
                    >
                      {r.parent_id ? "reply" : "top-level"}
                    </span>
                    {r.hidden && (
                      <span className="rm-admin-badge rm-admin-badge-hidden">hidden</span>
                    )}
                    {r.flag_count > 0 && (
                      <span
                        className="rm-admin-badge rm-admin-badge-flagged"
                        data-testid="admin-row-flagcount"
                      >
                        flagged ×{r.flag_count}
                      </span>
                    )}
                  </div>
                  <p className="rm-admin-row-body">{r.content}</p>
                  <div className="rm-admin-row-actions">
                    {r.hidden ? (
                      <button
                        type="button"
                        className="rm-cm-link"
                        disabled={busyId === r.id}
                        onClick={() => action(r.id, "unhide")}
                        data-testid={`admin-action-unhide-${r.id}`}
                      >
                        {busyId === r.id ? "…" : "Restore"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="rm-cm-link"
                        disabled={busyId === r.id}
                        onClick={() => action(r.id, "hide")}
                        data-testid={`admin-action-hide-${r.id}`}
                      >
                        {busyId === r.id ? "…" : "Hide"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="rm-cm-link rm-cm-link-muted rm-admin-danger"
                      disabled={busyId === r.id}
                      onClick={() => action(r.id, "delete")}
                      data-testid={`admin-action-delete-${r.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </section>
    </main>
  );
}
